import { cn } from '@quadralize/utils'
import * as SelectPrimitive from '@radix-ui/react-select'
import { AltArrowDown, CheckCircle } from '@solar-icons/react'
import { X } from 'lucide-react'
import * as React from 'react'
import { ScrollArea } from '../../data-display/scroll-area/scroll-area'
import { LabelWrapper } from '../label/label'

const Select = ({
  label,
  required,
  error,
  id,
  children,
  helperText,
  className,
  ...props
}: SelectPrimitive.SelectProps & {
  label?: string
  required?: boolean
  error?: boolean
  id?: string
  helperText?: React.ReactNode
  className?: string
}) => {
  const generatedId = React.useId()
  const selectId = id || generatedId

  return (
    <LabelWrapper
      label={label}
      id={selectId}
      required={required}
      error={error}
      helperText={helperText}
      className={className}
    >
      <SelectPrimitive.Root {...props}>{children}</SelectPrimitive.Root>
    </LabelWrapper>
  )
}

const SelectGroup = SelectPrimitive.Group

const SelectValue = SelectPrimitive.Value

const SelectTrigger = React.forwardRef<
  React.ComponentRef<typeof SelectPrimitive.Trigger>,
  React.ComponentPropsWithoutRef<typeof SelectPrimitive.Trigger> & {
    showIcon?: boolean
    onClear?: (e: React.MouseEvent | React.KeyboardEvent) => void
    error?: boolean
  }
>(({ className, children, showIcon = true, onClear, error, ...props }, ref) => (
  <SelectPrimitive.Trigger ref={ref} asChild {...props}>
    <button
      type="button"
      className={cn(
        'group flex h-11 w-full items-center justify-between rounded-md border border-input-border bg-input-bg px-3 py-2 text-sm text-text-primary ring-offset-bg-base placeholder:text-text-tertiary focus:outline-none focus:ring-1 focus:ring-focus-ring disabled:cursor-not-allowed disabled:opacity-50 hover:border-input-border-hover data-placeholder:text-text-tertiary cursor-pointer',
        error && 'border-error focus:ring-error',
        className,
      )}
    >
      <div className="flex-1 text-left min-w-0 antialiased">{children}</div>
      <div className="flex items-center shrink-0 ml-2">
        {onClear ? (
          <>
            {/* biome-ignore lint: nested interactive elements are not allowed */}
            <div
              role="button"
              tabIndex={-1}
              onClick={(e) => {
                e.preventDefault()
                e.stopPropagation()
                onClear?.(e)
              }}
              onPointerDown={(e) => {
                e.stopPropagation()
              }}
              onKeyDown={(e) => {
                if (e.key === 'Enter' || e.key === ' ') {
                  e.preventDefault()
                  e.stopPropagation()
                  onClear?.(e)
                }
              }}
              className="flex group-data-placeholder:hidden text-text-tertiary hover:text-text-primary focus:outline-none transition-colors cursor-pointer"
            >
              <X className="h-4 w-4 opacity-70 hover:opacity-100" strokeWidth={3} />
            </div>
            <SelectPrimitive.Icon asChild className="hidden group-data-placeholder:flex">
              <AltArrowDown className="h-4 w-4 opacity-50" />
            </SelectPrimitive.Icon>
          </>
        ) : (
          showIcon && (
            <SelectPrimitive.Icon asChild>
              <AltArrowDown className="h-4 w-4 opacity-50" />
            </SelectPrimitive.Icon>
          )
        )}
      </div>
    </button>
  </SelectPrimitive.Trigger>
))
SelectTrigger.displayName = SelectPrimitive.Trigger.displayName

const SelectContent = React.forwardRef<
  React.ComponentRef<typeof SelectPrimitive.Content>,
  React.ComponentPropsWithoutRef<typeof SelectPrimitive.Content>
>(({ className, children, position = 'popper', ...props }, ref) => (
  <SelectPrimitive.Portal>
    <SelectPrimitive.Content
      ref={ref}
      className={cn(
        'relative z-50 min-w-(--radix-select-trigger-width) overflow-hidden rounded-md border border-border-default bg-bg-elevated text-text-primary animate-in fade-in-80',
        position === 'popper' && 'translate-y-1',
        className,
      )}
      position={position}
      {...props}
    >
      <ScrollArea
        className="h-auto *:data-radix-scroll-area-viewport:max-h-96 w-full overflow-x-hidden"
        type="auto"
      >
        <SelectPrimitive.Viewport className={cn('p-1', position === 'popper' && 'w-full')}>
          {children}
        </SelectPrimitive.Viewport>
      </ScrollArea>
    </SelectPrimitive.Content>
  </SelectPrimitive.Portal>
))
SelectContent.displayName = SelectPrimitive.Content.displayName

const SelectLabel = React.forwardRef<
  React.ComponentRef<typeof SelectPrimitive.Label>,
  React.ComponentPropsWithoutRef<typeof SelectPrimitive.Label>
>(({ className, ...props }, ref) => (
  <SelectPrimitive.Label
    ref={ref}
    className={cn('px-2 py-1.5 text-sm font-semibold text-text-primary', className)}
    {...props}
  />
))
SelectLabel.displayName = SelectPrimitive.Label.displayName

const SelectItem = React.forwardRef<
  React.ComponentRef<typeof SelectPrimitive.Item>,
  React.ComponentPropsWithoutRef<typeof SelectPrimitive.Item>
>(({ className, children, ...props }, ref) => (
  <SelectPrimitive.Item
    ref={ref}
    className={cn(
      'relative flex w-full cursor-default select-none items-center rounded-sm py-1.5 pl-2 pr-8 text-sm outline-none focus:bg-bg-subtle focus:text-text-primary data-disabled:pointer-events-none data-disabled:opacity-50',
      className,
    )}
    {...props}
  >
    <span className="absolute right-2 flex h-3.5 w-3.5 items-center justify-center">
      <SelectPrimitive.ItemIndicator>
        <CheckCircle className="h-4 w-4" />
      </SelectPrimitive.ItemIndicator>
    </span>
    <SelectPrimitive.ItemText>{children}</SelectPrimitive.ItemText>
  </SelectPrimitive.Item>
))
SelectItem.displayName = SelectPrimitive.Item.displayName

const SelectSeparator = React.forwardRef<
  React.ComponentRef<typeof SelectPrimitive.Separator>,
  React.ComponentPropsWithoutRef<typeof SelectPrimitive.Separator>
>(({ className, ...props }, ref) => (
  <SelectPrimitive.Separator
    ref={ref}
    className={cn('-mx-1 my-1 h-px bg-border-subtle', className)}
    {...props}
  />
))
SelectSeparator.displayName = SelectPrimitive.Separator.displayName

export {
  Select,
  SelectContent,
  SelectGroup,
  SelectItem,
  SelectLabel,
  SelectSeparator,
  SelectTrigger,
  SelectValue,
}
